import { X, CreditCard, Banknote, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency } from '@/lib/dateUtils';

interface RevenueDetailModalProps {
  totalPaymentsUSD: number;
  totalGross: number;
  totalStripeFees: number;
  totalRefunds: number;
  debitGross: number;
  creditGross: number;
  debitCount: number;
  creditCount: number;
  paymentsCount: number;
  bankRate: number;
  creditCardRate: number;
  blackMarketRate: number;
  bankCostMVR: number;
  exchangeRevenueMVR: number;
  exchangeProfitMVR: number;
  onClose: () => void;
}

export default function RevenueDetailModal({
  totalPaymentsUSD, totalGross, totalStripeFees, totalRefunds,
  debitGross, creditGross, debitCount, creditCount, paymentsCount,
  bankRate, creditCardRate, blackMarketRate,
  bankCostMVR, exchangeRevenueMVR, exchangeProfitMVR,
  onClose,
}: RevenueDetailModalProps) {
  const debitBankCost = debitGross * bankRate;
  const creditBankCost = creditGross * creditCardRate;
  const debitPct = totalGross > 0 ? (debitGross / totalGross * 100) : 0;
  const creditPct = totalGross > 0 ? (creditGross / totalGross * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="glass-modal rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp size={16} className="text-emerald-400" />
            </div>
            <h2 className="text-base font-bold text-white">Revenue Breakdown</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg glass-hover">
            <X size={18} className="text-white" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          <div className="glass-inner rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-white uppercase tracking-wider">Net Revenue</span>
              <span className="text-lg font-bold text-emerald-400">${totalPaymentsUSD.toFixed(2)}</span>
            </div>
            <div className="space-y-2">
              <DetailRow label="Gross Volume" value={`$${totalGross.toFixed(2)}`} icon={ArrowUpRight} iconColor="text-emerald-400" />
              <DetailRow label="Stripe Fees" value={`-$${totalStripeFees.toFixed(2)}`} icon={ArrowDownRight} iconColor="text-amber-500" />
              {totalRefunds > 0 && <DetailRow label="Refunds" value={`-$${totalRefunds.toFixed(2)}`} icon={ArrowDownRight} iconColor="text-red-500" />}
              <DetailRow label="Total Payments" value={paymentsCount.toString()} icon={CreditCard} iconColor="text-sky-400" />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-white uppercase tracking-wider mb-3 px-1">Card Type Breakdown</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="glass-inner rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-sky-500/10 flex items-center justify-center">
                    <CreditCard size={13} className="text-sky-400" />
                  </div>
                  <span className="text-xs font-semibold text-white">Debit Cards</span>
                </div>
                <p className="text-lg font-bold text-white">${debitGross.toFixed(2)}</p>
                <p className="text-[11px] text-white/60 mt-0.5">{debitCount} payments</p>
                <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full transition-all duration-500" style={{ width: `${debitPct}%` }} />
                </div>
                <p className="text-[10px] text-white/50 mt-1">{debitPct.toFixed(1)}% of gross</p>
              </div>
              <div className="glass-inner rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <CreditCard size={13} className="text-amber-500" />
                  </div>
                  <span className="text-xs font-semibold text-white">Credit Cards</span>
                </div>
                <p className="text-lg font-bold text-white">${creditGross.toFixed(2)}</p>
                <p className="text-[11px] text-white/60 mt-0.5">{creditCount} payments</p>
                <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${creditPct}%` }} />
                </div>
                <p className="text-[10px] text-white/50 mt-1">{creditPct.toFixed(1)}% of gross</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-white uppercase tracking-wider mb-3 px-1">MVR Conversion</h3>
            <div className="glass-inner rounded-xl p-4 space-y-0">
              <div className="flex justify-between items-center py-2.5 border-b border-white/10">
                <div>
                  <span className="text-sm text-white">Debit Bank Cost</span>
                  <p className="text-[10px] text-white/50">${debitGross.toFixed(2)} x {bankRate.toFixed(2)} MVR</p>
                </div>
                <span className="text-sm font-semibold text-amber-500 tabular-nums">{formatCurrency(debitBankCost, 'MVR')}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-white/10">
                <div>
                  <span className="text-sm text-white">Credit Bank Cost</span>
                  <p className="text-[10px] text-white/50">${creditGross.toFixed(2)} x {creditCardRate.toFixed(2)} MVR</p>
                </div>
                <span className="text-sm font-semibold text-amber-500 tabular-nums">{formatCurrency(creditBankCost, 'MVR')}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-white/10">
                <div>
                  <span className="text-sm text-white font-medium">Total Bank Cost</span>
                  <p className="text-[10px] text-white/50">Debit + Credit cost</p>
                </div>
                <span className="text-sm font-bold text-amber-500 tabular-nums">{formatCurrency(bankCostMVR, 'MVR')}</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-white/10">
                <div>
                  <span className="text-sm text-white">Exchange Revenue</span>
                  <p className="text-[10px] text-white/50">${totalPaymentsUSD.toFixed(2)} x {blackMarketRate.toFixed(2)} MVR</p>
                </div>
                <span className="text-sm font-semibold text-white tabular-nums">{formatCurrency(exchangeRevenueMVR, 'MVR')}</span>
              </div>
              <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-white/10">
                <div className="flex items-center gap-2">
                  <Banknote size={14} className="text-emerald-400" />
                  <span className="text-sm text-white font-bold">Exchange Profit</span>
                </div>
                <span className={`text-lg font-bold tabular-nums ${exchangeProfitMVR >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
                  {formatCurrency(exchangeProfitMVR, 'MVR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, icon: Icon, iconColor }: {
  label: string; value: string; icon: typeof ArrowUpRight; iconColor: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={12} className={iconColor} />
        <span className="text-xs text-white/70">{label}</span>
      </div>
      <span className="text-xs font-semibold text-white tabular-nums">{value}</span>
    </div>
  );
}
